import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { useInvoices } from '../lib/queries'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, thaiDateTime } from '../lib/format'

/** All amounts below are integer satang — never baht, never floats. */

interface PaymentRow {
  id: string
  invoice_id: string
  invoice_number?: string
  provider: string
  provider_ref: string
  amount_satang: number
  currency: string
  status: string
  received_at: string
}

interface SecretView {
  secret: string
  header: string
  webhook_path: string
  webhook_url: string
  algorithm: string
}

interface MockResult {
  received: boolean
  duplicate: boolean
  payment: PaymentRow
  reconciliation: {
    invoice_id: string
    total_satang: number
    paid_satang: number
    outstanding_satang: number
    status: string
    invoice_status: string
  }
}

const providerLabel: Record<string, string> = { stripe: 'Stripe', omise: 'Omise', mock: 'จำลอง' }
const payStatusLabel: Record<string, string> = {
  pending: 'รอผล', succeeded: 'สำเร็จ', failed: 'ล้มเหลว', refunded: 'คืนเงิน',
}
const invoiceStatusLabel: Record<string, string> = {
  open: 'รอชำระ', paid: 'ชำระแล้ว', draft: 'ฉบับร่าง', void: 'ยกเลิก',
}
const reconLabel: Record<string, string> = { paid: 'ครบแล้ว', partial: 'บางส่วน' }

export function Payments() {
  const { me } = useAuth()
  const canManage = me?.role === 'owner' || me?.role === 'admin'
  const qc = useQueryClient()

  const invoices = useInvoices()
  const [invoiceFilter, setInvoiceFilter] = useState('')

  const payments = useQuery({
    queryKey: ['payments', invoiceFilter],
    queryFn: () => list<PaymentRow>(`/v1/payments?limit=200${invoiceFilter ? `&invoice_id=${invoiceFilter}` : ''}`),
    staleTime: 10_000,
  })

  const secret = useQuery({
    queryKey: ['payment-secret'],
    queryFn: () => api<SecretView>('/v1/payments/secret'),
    enabled: Boolean(canManage),
    staleTime: 60_000,
  })

  const mock = useMutation({
    mutationFn: (invoiceId: string) => apiWrite<MockResult>('/v1/payments/mock', 'POST', { invoice_id: invoiceId }),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['payments'] })
      void qc.invalidateQueries({ queryKey: ['invoices'] })
      void qc.invalidateQueries({ queryKey: ['billing'] })
    },
  })

  const rotate = useMutation({
    mutationFn: () => apiWrite<SecretView>('/v1/payments/secret', 'POST', {}),
    onSuccess: (data) => qc.setQueryData(['payment-secret'], data),
  })

  const [copied, setCopied] = useState<'url' | 'secret' | null>(null)
  const copy = async (kind: 'url' | 'secret', value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(kind)
      window.setTimeout(() => setCopied(null), 2000)
    } catch {
      setCopied(null)
    }
  }

  const invoiceRows = invoices.data ?? []
  const paymentRows = payments.data ?? []

  // Net settled per invoice: succeeded minus refunded. Pending and failed never count.
  const settled = useMemo(() => {
    const map = new Map<string, number>()
    for (const p of paymentRows) {
      if (p.status !== 'succeeded' && p.status !== 'refunded') continue
      const delta = p.status === 'refunded' ? -p.amount_satang : p.amount_satang
      map.set(p.invoice_id, (map.get(p.invoice_id) ?? 0) + delta)
    }
    return map
  }, [paymentRows])

  const totals = useMemo(() => {
    let invoiced = 0
    let collected = 0
    let outstanding = 0
    let paidCount = 0
    for (const inv of invoiceRows) {
      if (inv.status === 'void' || inv.status === 'draft') continue
      const paid = Math.max(0, settled.get(inv.id) ?? 0)
      invoiced += inv.total_satang
      collected += Math.min(paid, inv.total_satang)
      outstanding += Math.max(0, inv.total_satang - paid)
      if (inv.total_satang > 0 && paid >= inv.total_satang) paidCount += 1
    }
    return { invoiced, collected, outstanding, paidCount }
  }, [invoiceRows, settled])

  const invoiceNumbers = new Map(invoiceRows.map((i) => [i.id, i.number]))

  return (
    <>
      <PageHead
        title="การชำระเงิน"
        sub="กระทบยอดใบแจ้งหนี้จากเหตุการณ์ผู้ให้บริการ · บันทึกเป็นสตางค์ · ตรวจลายเซ็น HMAC ทุกครั้ง"
        actions={canManage ? (
          <button className="btn" disabled={rotate.isPending} onClick={() => rotate.mutate()}>
            {rotate.isPending ? 'กำลังสร้าง…' : 'สร้าง secret ใหม่'}
          </button>
        ) : undefined}
      />

      <div className="grid-4 stagger">
        <Stat label="ยอดที่ยังไม่ได้รับชำระ" value={<span className="tnum">{money(totals.outstanding)}</span>}
          foot={`จาก ${invoiceRows.filter((i) => i.status === 'open').length} ใบที่เปิดอยู่`} />
        <Stat label="รับชำระแล้ว" value={<span className="tnum">{money(totals.collected)}</span>}
          foot={`จากยอดออกใบแจ้งหนี้ ${money(totals.invoiced)}`} />
        <Stat label="ใบที่ชำระครบ" value={totals.paidCount} unit="ใบ"
          foot={`ทั้งหมด ${invoiceRows.length} ใบ`} />
        <Stat label="รายการชำระ" value={paymentRows.length} unit="รายการ"
          foot="นับเฉพาะรายการที่บันทึกแล้ว ไม่นับ event ซ้ำ" />
      </div>

      <div className="card card-pad" style={{ marginTop: 16 }}>
        <div className="row">
          <span className="card-label">กระทบยอดตามใบแจ้งหนี้</span>
          <span className="chip" style={{ marginLeft: 'auto' }}>{invoiceRows.length} ใบ</span>
        </div>
        {invoices.isLoading || payments.isLoading ? <div style={{ marginTop: 14 }}><Skel h={160} /></div> : invoiceRows.length === 0 ? (
          <div style={{ marginTop: 14 }}>
            <Empty title="ยังไม่มีใบแจ้งหนี้" body="ออกใบแจ้งหนี้ที่หน้าการเรียกเก็บเงินก่อน แล้วจึงบันทึกการชำระ" />
          </div>
        ) : (
          <div className="table-wrap" style={{ marginTop: 12 }}>
            <table className="table data">
              <thead>
                <tr>
                  <th>เลขที่</th><th>ยอดรวม</th><th>รับแล้ว</th><th>คงค้าง</th>
                  <th style={{ width: 130 }}>ความคืบหน้า</th><th>สถานะ</th><th />
                </tr>
              </thead>
              <tbody>
                {invoiceRows.map((inv) => {
                  const paid = Math.max(0, settled.get(inv.id) ?? 0)
                  const outstanding = Math.max(0, inv.total_satang - paid)
                  const recon = inv.total_satang > 0 && paid >= inv.total_satang ? 'paid' : 'partial'
                  const pct = inv.total_satang > 0 ? Math.min(100, Math.round((paid / inv.total_satang) * 100)) : 0
                  return (
                    <tr key={inv.id} style={{ cursor: 'default' }}>
                      <td className="mono" style={{ whiteSpace: 'nowrap' }}>{inv.number}</td>
                      <td className="num tnum">{money(inv.total_satang)}</td>
                      <td className="num tnum">{money(Math.min(paid, inv.total_satang))}</td>
                      <td className="num tnum">{money(outstanding)}</td>
                      <td>
                        <div className="row" style={{ fontSize: 11.5 }}>
                          <span className="tnum">{pct}%</span>
                        </div>
                        <div className="bar" style={{ height: 5, marginTop: 6 }}>
                          <i style={{ width: `${Math.max(2, pct)}%`, background: recon === 'paid' ? 'var(--color-emerald-soft)' : 'var(--color-gold)' }} />
                        </div>
                      </td>
                      <td>
                        <span className={`chip${recon === 'paid' ? ' gold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                          {reconLabel[recon]}{inv.status === 'open' ? ` · ${invoiceStatusLabel.open}` : ''}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {canManage && outstanding > 0 ? (
                          <button className="btn" disabled={mock.isPending}
                            onClick={() => mock.mutate(inv.id)}>
                            {mock.isPending && mock.variables === inv.id ? 'กำลังจำลอง…' : 'จำลองการชำระ'}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        {mock.isError ? (
          <div className="form-error" style={{ marginTop: 12 }}>
            {mock.error instanceof Error ? mock.error.message : 'จำลองการชำระไม่สำเร็จ'}
          </div>
        ) : null}
        {mock.isSuccess ? (
          <div className="dim" style={{ fontSize: 12, marginTop: 12 }}>
            บันทึกการชำระแล้ว · ใบแจ้งหนี้สถานะ {invoiceStatusLabel[mock.data.reconciliation.invoice_status] ?? mock.data.reconciliation.invoice_status}
            {' · '}คงค้าง {money(mock.data.reconciliation.outstanding_satang)}
            {mock.data.duplicate ? ' · event ซ้ำจึงไม่ถูกนับเพิ่ม' : ''}
          </div>
        ) : null}
      </div>

      <div className="grid-main" style={{ marginTop: 16 }}>
        <div className="card card-pad">
          <div className="card-label">ปลายทางรับแจ้งชำระ</div>
          {!canManage ? (
            <div className="dim" style={{ fontSize: 12.5, marginTop: 14, lineHeight: 1.9 }}>
              เฉพาะเจ้าของและผู้ดูแลระบบเท่านั้นที่ดู secret สำหรับเชื่อมต่อผู้ให้บริการได้
            </div>
          ) : secret.isLoading ? (
            <div style={{ marginTop: 14 }}><Skel h={130} /></div>
          ) : secret.isError ? (
            <div className="form-error" style={{ marginTop: 14 }}>โหลดค่าเชื่อมต่อไม่สำเร็จ</div>
          ) : secret.data ? (
            <div style={{ marginTop: 14 }}>
              <div className="field">
                <label htmlFor="webhook-url">Webhook URL</label>
                <div className="row" style={{ gap: 8 }}>
                  <input id="webhook-url" className="input mono" readOnly value={secret.data.webhook_url}
                    onFocus={(e) => e.currentTarget.select()} />
                  <button className="btn" onClick={() => copy('url', secret.data!.webhook_url)}>
                    {copied === 'url' ? 'คัดลอกแล้ว' : 'คัดลอก'}
                  </button>
                </div>
              </div>
              <div className="field">
                <label htmlFor="webhook-secret">Secret สำหรับลงนาม</label>
                <div className="row" style={{ gap: 8 }}>
                  <input id="webhook-secret" className="input mono" readOnly value={secret.data.secret}
                    onFocus={(e) => e.currentTarget.select()} />
                  <button className="btn" onClick={() => copy('secret', secret.data!.secret)}>
                    {copied === 'secret' ? 'คัดลอกแล้ว' : 'คัดลอก'}
                  </button>
                </div>
              </div>
              <div className="dim" style={{ fontSize: 11.5, lineHeight: 1.9, marginTop: 6 }}>
                ส่ง header <span className="mono">{secret.data.header}</span> เป็นค่า HMAC-SHA256 (hex) ของ body ดิบ
                · ระบบตอบ 200 แม้เป็น event ซ้ำ แต่จะไม่บันทึกซ้ำ
                · ใบแจ้งหนี้ที่ไม่รู้จักตอบ 422 และลายเซ็นผิดตอบ 401
              </div>
              {rotate.isError ? (
                <div className="form-error" style={{ marginTop: 10 }}>
                  {rotate.error instanceof Error ? rotate.error.message : 'สร้าง secret ใหม่ไม่สำเร็จ'}
                </div>
              ) : null}
              <div className="dim" style={{ fontSize: 11.5, marginTop: 10 }}>
                สร้าง secret ใหม่แล้ว ผู้ให้บริการเดิมจะส่ง event ไม่ผ่านจนกว่าจะอัปเดตค่า
              </div>
            </div>
          ) : null}
        </div>

        <div className="card card-pad">
          <div className="card-label">หลักการกระทบยอด</div>
          <ul className="dim" style={{ fontSize: 12.5, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
            <li>ผู้ให้บริการส่ง POST พร้อมลายเซ็นของ body ดิบ ระบบตรวจก่อนแตะฐานข้อมูล</li>
            <li>รหัสอ้างอิงจากผู้ให้บริการถูกใช้เพียงครั้งเดียวต่อผู้ให้บริการหนึ่งราย</li>
            <li>ยอดรับสุทธิ = รายการสำเร็จ − รายการคืนเงิน รายการรอผลและล้มเหลวไม่ถูกนับ</li>
            <li>ใบที่ชำระไม่ครบยังเป็น “รอชำระ” ในฐานข้อมูล แต่หน้านี้แสดง “บางส่วน”</li>
            <li>เงินเก็บเป็นสตางค์จำนวนเต็มทุกเส้นทาง ไม่มีการปัดเศษทศนิยม</li>
          </ul>
        </div>
      </div>

      <div style={{ marginTop: 22 }}>
        <div className="row" style={{ marginBottom: 10 }}>
          <span className="card-label">รายการชำระล่าสุด</span>
          <select aria-label="กรองตามสถานะ" className="input" style={{ marginLeft: 'auto', maxWidth: 280 }}
            value={invoiceFilter} onChange={(e) => setInvoiceFilter(e.target.value)}>
            <option value="">ทุกใบแจ้งหนี้</option>
            {invoiceRows.map((i) => <option key={i.id} value={i.id}>{i.number}</option>)}
          </select>
        </div>

        {payments.isLoading ? <Skel h={180} /> : paymentRows.length === 0 ? (
          <Empty title="ยังไม่มีรายการชำระ" body="เมื่อผู้ให้บริการส่ง event ที่ลงนามถูกต้อง รายการจะปรากฏที่นี่ทันที" />
        ) : (
          <div className="table-wrap">
            <table className="table data">
              <thead>
                <tr>
                  <th>เวลา</th><th>ใบแจ้งหนี้</th><th>ผู้ให้บริการ</th><th>รหัสอ้างอิง</th>
                  <th>จำนวน</th><th>สถานะ</th>
                </tr>
              </thead>
              <tbody>
                {paymentRows.map((p) => (
                  <tr key={p.id} style={{ cursor: 'default' }}>
                    <td className="dim" style={{ whiteSpace: 'nowrap' }}>{thaiDateTime(p.received_at)}</td>
                    <td className="mono" style={{ whiteSpace: 'nowrap' }}>
                      {p.invoice_number ?? invoiceNumbers.get(p.invoice_id) ?? p.invoice_id.slice(0, 8)}
                    </td>
                    <td><span className="chip">{providerLabel[p.provider] ?? p.provider}</span></td>
                    <td className="mono dim" title={p.provider_ref} style={{ whiteSpace: 'nowrap' }}>
                      {p.provider_ref.length > 26 ? `${p.provider_ref.slice(0, 26)}…` : p.provider_ref}
                    </td>
                    <td className="num tnum">{money(p.amount_satang)}</td>
                    <td>
                      <span className={`chip${p.status === 'succeeded' ? ' gold' : ''}`} style={{ whiteSpace: 'nowrap' }}>
                        {payStatusLabel[p.status] ?? p.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  )
}
